import { useCallback, useEffect, useRef, useState } from "react";
import type { IssueDetail, IssueEditable } from "./segment-types";

/**
 * The fields the editor can change — one entry per undo step.
 *
 * Sections and categories belong here as much as the subject does. Without
 * them undo covered only the text fields, so deleting a section, reordering
 * one, rewriting a link's description or changing a picture's position could
 * not be taken back: ⌘Z appeared to do nothing.
 */
export type Snapshot = Required<Pick<IssueDetail,
  "title" | "subject">> & Pick<IssueDetail,
  "notes" | "blocks" | "sections" | "categories" | "preheader" | "sender_name" | "reply_to" | "utm">;

export function snapshotOf(issue: IssueDetail): Snapshot {
  return {
    title: issue.title,
    subject: issue.subject,
    notes: issue.notes,
    blocks: issue.blocks,
    sections: issue.sections,
    categories: issue.categories,
    preheader: issue.preheader,
    sender_name: issue.sender_name,
    reply_to: issue.reply_to,
    utm: issue.utm,
  };
}

const LIMIT = 60;

/**
 * Undo/redo for a block editor.
 *
 * Rearranging blocks is destructive and easy to get wrong, so this is not
 * optional. Typing is coalesced: consecutive edits inside `coalesceMs` replace
 * the top of the stack instead of pushing, so undo steps back a phrase rather
 * than a keystroke.
 */
export function useEditorHistory(
  issue: IssueDetail,
  apply: (fields: IssueEditable) => void,
  { coalesceMs = 600 }: { coalesceMs?: number } = {},
) {
  const past = useRef<Snapshot[]>([]);
  const future = useRef<Snapshot[]>([]);
  const current = useRef<Snapshot>(snapshotOf(issue));
  const lastPush = useRef(0);
  const restoring = useRef(false);
  const [, force] = useState(0);
  const rerender = () => force((n) => n + 1);

  // Track edits made outside this hook (block adds, drags, field typing).
  useEffect(() => {
    const next = snapshotOf(issue);
    if (JSON.stringify(next) === JSON.stringify(current.current)) return;
    if (restoring.current) {
      restoring.current = false;
      current.current = next;
      rerender();
      return;
    }
    const now = Date.now();
    // A burst of typing collapses into one step.
    if (now - lastPush.current > coalesceMs) {
      past.current = [...past.current, current.current].slice(-LIMIT);
      future.current = [];
    }
    lastPush.current = now;
    current.current = next;
    rerender();
  }, [issue, coalesceMs]);

  const undo = useCallback(() => {
    const prev = past.current.at(-1);
    if (!prev) return;
    past.current = past.current.slice(0, -1);
    future.current = [current.current, ...future.current].slice(0, LIMIT);
    restoring.current = true;
    apply(prev);
    rerender();
  }, [apply]);

  const redo = useCallback(() => {
    const next = future.current[0];
    if (!next) return;
    future.current = future.current.slice(1);
    past.current = [...past.current, current.current].slice(-LIMIT);
    restoring.current = true;
    apply(next);
    rerender();
  }, [apply]);

  return { undo, redo, canUndo: past.current.length > 0, canRedo: future.current.length > 0 };
}

/**
 * Autosave state. Every change already writes straight through to the store,
 * so this reports that rather than scheduling it — the risk was never lost
 * work, it was that nothing told you the work was safe.
 */
export function useSaveState(issue: IssueDetail) {
  const [state, setState] = useState<"idle" | "saving" | "saved">("idle");
  const [at, setAt] = useState<number | null>(null);
  const first = useRef(true);
  const key = JSON.stringify(snapshotOf(issue));

  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setState("saving");
    const t = window.setTimeout(() => {
      setState("saved");
      setAt(Date.now());
    }, 250);
    return () => window.clearTimeout(t);
  }, [key]);

  return { state, at };
}

/** "Saved just now" / "Saved 3m ago" — re-rendered by the caller's own ticks. */
export function savedLabel(at: number | null): string {
  if (at == null) return "";
  const m = Math.floor((Date.now() - at) / 60000);
  if (m < 1) return "Saved just now";
  if (m === 1) return "Saved 1 minute ago";
  if (m < 60) return `Saved ${m} minutes ago`;
  return "Saved";
}
